import React, { useEffect, useRef, useState } from 'react';
import { QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { getFeishuAppId, getFeishuRedirectUri } from '../../utils/feishu';
import './index.css';

export interface FeishuQRCodeProps {
  /** 回调地址路径，例如 'callback' */
  callbackPath?: string;
  /** 二维码容器ID，默认为 'feishu-qrcode' */
  containerId?: string;
  /** 二维码宽度，默认 250 */
  width?: string;
  /** 二维码高度，默认 280 */
  height?: string;
  /** 自定义样式 */
  style?: Record<string, any>;
  /** 登录成功后的回调 */
  onSuccess?: (tmpCode: string) => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
  /** 是否自动初始化，默认 true */
  autoInit?: boolean;
}

/**containerId
 * 飞书二维码登录组件
 * 
 * @example
 * ```tsx
 * <FeishuQRCode 
 *   callbackPath="callback"
 *   onSuccess={(tmpCode) => {
 *     console.log('登录成功', tmpCode);
 *   }}
 * />
 * ```
 */
const FeishuQRCode = ({
  callbackPath = 'callback',
  containerId = 'feishu-qrcode',
  width = '250',
  height = '280',
  style,
  onSuccess,
  onError,
  autoInit = true
}) => {
  const [status, setStatus] = useState('init' as 'init' | 'ready' | 'error');
  const [error, setError] = useState('');
  const qrLoginRef = useRef(null);
  const messageHandlerRef = useRef(null);
  const containerRef = useRef(null);

  /**
   * 初始化飞书二维码
   */
  const initQRCode = () => {
    try {
      setStatus('init');
      setError('');

      // 检查飞书 SDK 是否加载
      if (typeof (window as any).QRLogin === 'undefined') {
        const errorMsg = '飞书 SDK 未加载，请确保已引入飞书登录 JS SDK';
        setError(errorMsg);
        setStatus('error');
        onError?.(new Error(errorMsg));
        return;
      }

      const appId = getFeishuAppId();
      if (!appId) {
        const errorMsg = '未配置 VITE_OIDC_APPID 环境变量';
        setError(errorMsg);
        setStatus('error');
        onError?.(new Error(errorMsg));
        return;
      }

      // redirect_uri 须与飞书开放平台登记完全一致；本地开发时通过 getFeishuRedirectUri 走测试环境（见 VITE_APP_BASE_URL / VITE_FEISHU_REDIRECT_URI）
      const redirect_uri = getFeishuRedirectUri();

      const gotoUrl = `https://passport.feishu.cn/suite/passport/oauth/authorize?client_id=${encodeURIComponent(appId)}&redirect_uri=${encodeURIComponent(redirect_uri)}&response_type=code&state=success_login`;
      
      // 调试信息（开发环境）
      if ((import.meta as any).env?.MODE === 'development') {
        console.log('飞书登录参数:', {
          appId,
          redirect_uri: redirect_uri,
          gotoUrl,
        });
      }

      // 清理之前的实例
      if (qrLoginRef.current && containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      // 初始化二维码
      const QRLoginObj = (window as any).QRLogin({
        id: containerId,
        goto: gotoUrl,
        width: '300',
        height: '300',
        // 避免 scale 导致子像素插值、二维码边缘发糊
        style: '',
      });

      qrLoginRef.current = QRLoginObj;
      
      // 保存 gotoUrl 到 ref，确保在闭包中可以访问
      const gotoUrlRef = { current: gotoUrl };
      
      // 处理消息事件
      const handleMessage = (event: MessageEvent) => {
        console.log('收到 message 事件:', {
          origin: event.origin,
          data: event.data,
          source: event.source,
        });
        
        // 先检查是否有 QRLoginObj
        if (!QRLoginObj || !QRLoginObj.matchOrigin) {
          console.warn('QRLoginObj 不存在或没有 matchOrigin 方法');
          return;
        }
        
        const origin = event.origin;
        const isMatch = QRLoginObj.matchOrigin(origin);
        console.log('origin 匹配结果:', isMatch, 'origin:', origin);
        
        if (isMatch) {
          console.log('origin 匹配成功，处理登录逻辑');
          
          // 处理 ReturnUrl
          const ReturnUrl =
            new URLSearchParams(window.location.search).get('ReturnUrl') ||
            localStorage.getItem('ReturnUrl');
          if (ReturnUrl) {
            const decodedUrl = decodeURIComponent(ReturnUrl);
            localStorage.setItem('ReturnUrl', decodedUrl);
          }

          const loginTmpCode = event.data;
          console.log('获取到 tmpCode:', loginTmpCode);
          
          // 调用成功回调
          if (onSuccess) {
            console.log('调用 onSuccess 回调');
            // 
            const finalGotoUrl = `${gotoUrlRef.current}&tmp_code=${loginTmpCode}`;
            window.location.href = finalGotoUrl;
            onSuccess(loginTmpCode);
          } else {
            // 默认行为：跳转到授权页面
            const finalGotoUrl = `${gotoUrlRef.current}&tmp_code=${loginTmpCode}`;
            window.location.href = finalGotoUrl;
          }
        } else {
          console.log('origin 不匹配，忽略消息');
        }
      };

      // 移除旧的事件监听器
      if (messageHandlerRef.current) {
        console.log('移除旧的事件监听器');
        if (typeof window.removeEventListener !== 'undefined') {
          window.removeEventListener('message', messageHandlerRef.current, false);
        } else if (typeof (window as any).detachEvent !== 'undefined') {
          (window as any).detachEvent('onmessage', messageHandlerRef.current);
        }
      }

      // 添加新的事件监听器
      messageHandlerRef.current = handleMessage;
      console.log('添加 message 事件监听器');
      
      // 添加全局监听器用于调试
      const debugHandler = (event: MessageEvent) => {
        console.log('全局 message 事件:', {
          origin: event.origin,
          data: event.data,
          type: event.type,
        });
      };
      window.addEventListener('message', debugHandler, false);
      
      if (typeof window.addEventListener !== 'undefined') {
        window.addEventListener('message', handleMessage, false);
        console.log('事件监听器已添加');
      } else if (typeof (window as any).attachEvent !== 'undefined') {
        (window as any).attachEvent('onmessage', handleMessage);
        console.log('事件监听器已添加（IE模式）');
      }
      
      // 验证事件监听器是否添加成功
      console.log('事件监听器状态:', {
        hasListener: messageHandlerRef.current !== null,
        QRLoginObj: !!QRLoginObj,
        gotoUrl: gotoUrlRef.current,
      });

      setStatus('ready');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('初始化二维码失败');
      const errorMessage = error.message || '初始化二维码失败';
      setError(errorMessage);
      setStatus('error');
      onError?.(error);
      console.error('飞书二维码初始化失败:', {
        error: err,
        message: errorMessage,
        appId: getFeishuAppId(),
        redirect_uri: getFeishuRedirectUri(),
      });
    }
  };

  /**
   * 刷新二维码
   */
  const handleRefresh = () => {
    initQRCode();
  };

  // 自动初始化
  useEffect(() => {
    if (autoInit) {
      // 等待首屏 DOM 渲染完成后再初始化二维码
      const timer = setTimeout(() => {
        initQRCode();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoInit, callbackPath, containerId, width, height]);

  // 清理资源
  useEffect(() => {
    return () => {
      console.log('组件卸载，清理资源');
      // 移除事件监听器
      if (messageHandlerRef.current) {
        console.log('移除 message 事件监听器');
        if (typeof window.removeEventListener !== 'undefined') {
          window.removeEventListener('message', messageHandlerRef.current, false);
        } else if (typeof (window as any).detachEvent !== 'undefined') {
          (window as any).detachEvent('onmessage', messageHandlerRef.current);
        }
        messageHandlerRef.current = null;
      }

      // 清理二维码实例
      if (qrLoginRef.current && containerRef.current) {
        containerRef.current.innerHTML = '';
        qrLoginRef.current = null;
      }
    };
  }, []);

  return (
    <div className="feishu-qrcode-wrapper" style={style}>
      <div className="feishu-qrcode-container">
        {/* 二维码真正挂载的容器，始终渲染，保证 QRLogin 能找到 DOM 节点 */}
        <div
          ref={containerRef}
          id={containerId}
          className="feishu-qrcode-content"
        />

        {/* 状态遮罩层：初始化 / 错误 时覆盖在容器上方（失败遮罩逻辑保持，仅样式随外框统一） */}
        {status === 'init' && (
          <div className="feishu-qrcode-placeholder">
            <QrcodeOutlined style={{ fontSize: '80px', color: '#d9d9d9' }} />
            <p>正在生成二维码...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="feishu-qrcode-placeholder error">
            <QrcodeOutlined style={{ fontSize: '80px', color: '#ff4d4f' }} />
            <p>生成二维码失败</p>
            <p className="feishu-qrcode-tip-small">请点击下方按钮重试</p>
          </div>
        )}
      </div>
      {/* <p className="feishu-qrcode-hint">请使用飞书 App 扫一扫登录</p> */}
    </div>
  );
};

export default FeishuQRCode;

